/**
 * Translate a coupons-v2 tool call (name + args) into a human-readable
 * summary for the approval card. Officers are non-technical — raw JSON
 * is useless to them. This module produces a tiny structured object
 * the Vue template renders cleanly.
 */

export interface SummarySection {
  label: string;
  lines: string[];
}

export interface CallSummary {
  title: string;
  sections: SummarySection[];
}

const CURRENCY_DEFAULT = "SAR";

// ------------- Matcher rendering -------------

function describeMatcher(m: any): string {
  if (!m || typeof m !== "object") return "every item";
  if (m.match_all !== undefined) return "every item in the cart";
  if (m.match_none !== undefined) return "nothing";
  if (m.match_purchasable_id)
    return `product id ${shortId(m.match_purchasable_id.value ?? m.match_purchasable_id)}`;
  if (m.match_purchasable_code)
    return `product code '${m.match_purchasable_code.value ?? m.match_purchasable_code}'`;
  return "(unknown target)";
}

function shortId(id: any): string {
  if (!id) return "?";
  const s = String(id);
  return s.length > 16 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s;
}

// ------------- Condition rendering -------------

function describeCondition(c: any, depth = 0): string[] {
  if (!c || typeof c !== "object") return [];
  const keys = Object.keys(c);
  if (keys.length === 0) return [];
  const key = keys[0];
  const v = c[key];

  switch (key) {
    case "bool":
      return v ? ["Always applies"] : ["Never applies"];
    case "mpv": {
      const limit = v?.limit ?? 0;
      const amount = moneyFromHalala(limit);
      const currency = v?.currency ?? CURRENCY_DEFAULT;
      const vat = v?.include_vat ? " (incl. VAT)" : " (excl. VAT)";
      return [`Cart total at least ${amount} ${currency}${vat}`];
    }
    case "time_limited_availability": {
      const start = v?.[0] ? formatDate(v[0]) : "any time";
      const end = v?.[1] ? formatDate(v[1]) : "forever";
      return [`Valid from ${start} to ${end}`];
    }
    case "usage_limit": {
      const total = Array.isArray(v) ? v[0] : v?.limit;
      const perStore = Array.isArray(v) ? v[1] : v?.limit_for_store;
      return [`Max ${total} total uses, ${perStore} per store`];
    }
    case "store_id_in":
      return [
        `Only for store${v?.length === 1 ? "" : "s"}: ${(v ?? []).join(", ")}`,
      ];
    case "store_country_in":
      return [`Only in countries: ${(v ?? []).join(", ")}`];
    case "store_currency_is":
      return [`Store currency is ${v}`];
    case "purchase_currency_is":
      return [`Purchase currency is ${v}`];
    case "purchase_contains_item": {
      if (v?.purchasable_id)
        return [`Cart must contain product id ${shortId(v.purchasable_id)}`];
      if (v?.purchasable_code)
        return [`Cart must contain product code '${v.purchasable_code}'`];
      if (v?.purchasable_type)
        return [`Cart must contain item of type ${v.purchasable_type}`];
      return ["Cart must contain a specific item"];
    }
    case "intro_offer":
      return [`Merchant must be eligible for ${v?.target_package ?? "intro"} intro offer`];
    case "and":
    case "or": {
      const joiner = key === "and" ? "ALL of the following" : "ANY of the following";
      const subs = Array.isArray(v) ? v : [];
      if (subs.length === 0) return [joiner];
      if (subs.length === 1) return describeCondition(subs[0], depth + 1);
      const lines = [`${joiner}:`];
      for (const sub of subs) {
        for (const l of describeCondition(sub, depth + 1)) lines.push(`  • ${l}`);
      }
      return lines;
    }
    case "not": {
      const inner = describeCondition(v, depth + 1);
      return [`NOT (${inner.join("; ")})`];
    }
    default:
      return [`Condition: ${key}`];
  }
}

// ------------- Effect rendering -------------

function describeEffect(e: any, depth = 0): string[] {
  if (!e || typeof e !== "object") return [];
  const keys = Object.keys(e);
  if (keys.length === 0) return [];
  const key = keys[0];
  const v = e[key];

  if (key === "discount.relative" || key === "discount") {
    const percent = v?.discount_value ?? "?";
    const target = describeMatcher(v?.matcher);
    return [`${percent}% off on ${target}`];
  }
  if (key === "discount.absolute") {
    const amount = moneyFromHalala(v?.discount_value ?? 0);
    const currency = v?.discount_value_currency ?? CURRENCY_DEFAULT;
    const target = describeMatcher(v?.matcher);
    return [`${amount} ${currency} off on ${target}`];
  }
  if (key === "discount.post_vat") {
    const amount = moneyFromHalala(v?.discount_value ?? 0);
    const currency = v?.discount_value_currency ?? CURRENCY_DEFAULT;
    const target = describeMatcher(v?.matcher);
    return [`${amount} ${currency} off (after VAT) on ${target}`];
  }
  if (key === "bxgy") {
    const target = describeMatcher(v?.matcher);
    const y = Array.isArray(v?.gets_y) ? v.gets_y[0] : v?.gets_y;
    const yQty = Array.isArray(v?.gets_y) ? v.gets_y[1] : 1;
    return [`Buy ${target}, get product ${shortId(y)} × ${yQty} free`];
  }
  if (key === "free_item") {
    const id = Array.isArray(v) ? v[0] : v?.id;
    const qty = Array.isArray(v) ? v[1] : v?.quantity ?? 1;
    return [`Free item: product ${shortId(id)} × ${qty}`];
  }
  if (key === "group") {
    const items = Array.isArray(v?.items) ? v.items : [];
    if (items.length === 0) return ["Group of 0 effects (empty)"];
    const header = `Bundle of ${items.length} discount${items.length === 1 ? "" : "s"}${
      v?.sum_effects ? " (stacked)" : " (best only)"
    }:`;
    const lines = [header];
    for (const sub of items) {
      for (const l of describeEffect(sub, depth + 1)) lines.push(`  • ${l}`);
    }
    return lines;
  }
  if (key === "none" || key === "nothing") return ["No effect"];
  return [`Effect: ${key}`];
}

// ------------- Post-action rendering -------------

function describePostAction(pa: any): string[] {
  if (!pa || typeof pa !== "object") return [];
  const keys = Object.keys(pa);
  if (keys.length === 0) return [];
  const key = keys[0];
  const v = pa[key];

  if (key === "send_cashback") {
    if (v?.strategy === "purchase_value_percent") {
      return [`Cashback ${v.amount}% of purchase (after discount)`];
    }
    const amount = moneyFromHalala(v?.amount ?? 0);
    return [`Cashback ${amount} SAR (fixed)`];
  }
  if (key === "send_slack_notification") {
    return [`Send Slack notification: "${v?.text ?? ""}"`];
  }
  if (key === "group") {
    const items = Array.isArray(v?.group) ? v.group : [];
    if (items.length === 0) return ["(no post-actions)"];
    const lines: string[] = [];
    for (const sub of items) {
      for (const l of describePostAction(sub)) lines.push(l);
    }
    return lines;
  }
  return [`Post-action: ${key}`];
}

// ------------- Helpers -------------

/** Format a numeric money value with thousand separators (e.g. 50000 -> "50,000"). */
function money(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** Backend sends money in halala (1/100 SAR); convert then group with commas. */
function moneyFromHalala(halala: number): string {
  return money(halala / 100);
}

/** Group a raw (already-in-currency) amount with commas, leaving non-numeric input untouched. */
function moneyRaw(value: any): string {
  const n = Number(value);
  return Number.isFinite(n) ? money(n) : String(value);
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

// ------------- Top-level per-tool summarisers -------------

export function summarizeCall(toolName: string, args: any): CallSummary {
  const a = args ?? {};

  if (toolName === "action_couponv2_create_raw" || toolName === "action_couponv2_update_raw") {
    const body = a.body ?? {};
    const title =
      toolName === "action_couponv2_create_raw"
        ? `Create coupon${body.code ? ` "${body.code}"` : ""}`
        : `Update coupon${a.uuid ? ` ${shortId(a.uuid)}` : ""}`;

    const sections: SummarySection[] = [];
    if (body.description) {
      sections.push({ label: "Description", lines: [String(body.description)] });
    }
    if (body.condition) {
      sections.push({ label: "Applies when", lines: describeCondition(body.condition) });
    }
    if (body.effects) {
      sections.push({ label: "Gives", lines: describeEffect(body.effects) });
    }
    if (body.post_action) {
      sections.push({ label: "Then", lines: describePostAction(body.post_action) });
    }
    if (a.start_date || a.end_date) {
      sections.push({
        label: "Offer window",
        lines: [`${formatDate(a.start_date)} → ${formatDate(a.end_date)}`],
      });
    }
    // flags
    const flags: string[] = [];
    if (body.is_enabled === false) flags.push("Disabled from the start");
    if (body.is_hidden === true) flags.push("Hidden from listings");
    if (body.can_be_modified === false) flags.push("Not modifiable after create");
    if (flags.length) sections.push({ label: "Flags", lines: flags });
    return { title, sections };
  }

  if (toolName === "action_couponv2_create_percent_off") {
    const sections: SummarySection[] = [];
    const minCart = a.min_cart_value
      ? ` (min cart ${moneyRaw(a.min_cart_value)} ${a.min_cart_currency ?? CURRENCY_DEFAULT})`
      : "";
    const scope =
      a.applicable_to === "specific_products"
        ? ` on ${(a.product_ids ?? []).length} specific products`
        : " on every cart item";
    sections.push({
      label: "Gives",
      lines: [`${a.percent ?? "?"}% off${scope}${minCart}`],
    });
    if (a.start_date || a.end_date) {
      sections.push({
        label: "Offer window",
        lines: [`${formatDate(a.start_date)} → ${formatDate(a.end_date)}`],
      });
    }
    if (a.description) sections.push({ label: "Description", lines: [a.description] });
    return { title: `Create percent-off coupon "${a.code ?? ""}"`, sections };
  }

  if (toolName === "action_couponv2_create_fixed_amount") {
    const sections: SummarySection[] = [];
    const currency = a.currency ?? CURRENCY_DEFAULT;
    const minCart = a.min_cart_value
      ? ` (min cart ${moneyRaw(a.min_cart_value)} ${a.min_cart_currency ?? currency})`
      : "";
    const scope =
      a.applicable_to === "specific_products"
        ? ` on ${(a.product_ids ?? []).length} specific products`
        : " on every cart item";
    sections.push({
      label: "Gives",
      lines: [`${a.amount == null ? "?" : moneyRaw(a.amount)} ${currency} off${scope}${minCart}`],
    });
    if (a.start_date || a.end_date) {
      sections.push({
        label: "Offer window",
        lines: [`${formatDate(a.start_date)} → ${formatDate(a.end_date)}`],
      });
    }
    return { title: `Create fixed-amount coupon "${a.code ?? ""}"`, sections };
  }

  if (toolName === "action_couponv2_enable") {
    return {
      title: `Enable coupon ${shortId(a.uuid)}`,
      sections: [{ label: "Effect", lines: ["The coupon will be redeemable again."] }],
    };
  }
  if (toolName === "action_couponv2_disable") {
    return {
      title: `Disable coupon ${shortId(a.uuid)}`,
      sections: [{ label: "Effect", lines: ["The coupon will stop working immediately."] }],
    };
  }

  if (toolName === "action_couponv2_update_metadata") {
    const sections: SummarySection[] = [];
    if (a.code !== undefined) sections.push({ label: "New code", lines: [String(a.code)] });
    if (a.description !== undefined)
      sections.push({ label: "New description", lines: [String(a.description)] });
    if (a.is_enabled !== undefined)
      sections.push({ label: "Enabled", lines: [a.is_enabled ? "Yes" : "No"] });
    if (a.is_hidden !== undefined)
      sections.push({ label: "Hidden", lines: [a.is_hidden ? "Yes" : "No"] });
    return { title: `Update coupon ${shortId(a.uuid)} metadata`, sections };
  }

  if (toolName === "action_couponv2_add_offer_window") {
    return {
      title: `Add offer window to coupon ${shortId(a.uuid)}`,
      sections: [
        {
          label: "Window",
          lines: [`${formatDate(a.start_date)} → ${formatDate(a.end_date)}`],
        },
      ],
    };
  }

  // Fallback for any future / unhandled action — show the tool name and best-effort keys.
  const keys = Object.keys(a).filter((k) => typeof a[k] !== "object");
  const lines = keys.map((k) => `${k}: ${String(a[k]).slice(0, 120)}`);
  return {
    title: toolName.replace(/^action_/, "").replace(/_/g, " "),
    sections: lines.length ? [{ label: "Parameters", lines }] : [],
  };
}
